/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";

import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from "antd";
import moment from "moment";
import PropTypes from "prop-types";

import {
  createRefernEarnProductNew,
  editRefernEarnProductNew,
} from "../Pages/api";

const RefernEarnAddEditProductModal = ({
  visible,
  onClose,
  onSave,
  mode,
  product,
}) => {
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({});
  const [enableValidity, setEnableValidity] = useState(false);
  const [loading, setLoading] = useState(false);

  // Calculate Total Price
  const calculateTotalPrice = (onetime, sub) => {
    const total = parseFloat(onetime || 0) + parseFloat(sub || 0);
    return Math.round(total * 100) / 100;
  };

  const handleCreateEditRefernEarnProducts = async (values) => {
    setLoading(true);
    try {
      let res;

      if (mode === "add") {
        res = await createRefernEarnProductNew(values);
      } else {
        res = await editRefernEarnProductNew(product?._id, values);
      }

      if (res?.code === 201 || res?.code === 200) {
        form.resetFields();
        onSave();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "edit") {
      form.setFieldsValue({
        title: product?.productName,
        isExclusive: product?.isExclusive,
        platformSetup: product?.platformSetup,
        tier: product?.tier,
        onetimeFee: product?.onetimeFee,
        subscriptionFee: product?.subscriptionFee,
        totalProductPrice: calculateTotalPrice(
          product?.onetimeFee || 0,
          product?.subscriptionFee || 0
        ),
        paymentPlan: product?.paymentPlan,
        productLink: product?.productLink,
        mango: product?.mango,
        revenueShare: product?.revenueShare,
        status: product?.status,
        expireAt: product?.expireAt ? moment(product?.expireAt) : "",
      });
      if (product?.expireAt) {
        setEnableValidity(true);
      }
      setFormValues({
        ...product,
      });
    } else if (product && product.user) {
      setFormValues((prevState) => ({
        ...prevState,
        user: product.user,
      }));
    }
  }, [mode, product]);

  return (
    <Modal
      title={mode === "add" ? "Add Product" : "Edit Product"}
      visible={visible}
      onCancel={() => {
        form.resetFields();
        setFormValues({});
        onClose();
      }}
      destroyOnClose
      footer={[
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => {
            form.validateFields().then((values) => {
              // find if oneTimeFee or subscriptionFee is present
              // if yes then convert it to number

              const validatedValues = values;

              if (values.onetimeFee) {
                validatedValues.onetimeFee = parseFloat(values.onetimeFee);
              }

              if (values.subscriptionFee) {
                validatedValues.subscriptionFee = parseFloat(
                  values.subscriptionFee
                );
              }

              if (!enableValidity) {
                validatedValues.expireAt = null;
              }

              if (formValues?.user?._id) {
                validatedValues.user = formValues?.user?._id;
              }

              handleCreateEditRefernEarnProducts(validatedValues);
            });
          }}
          style={{ width: "100%" }}
        >
          Save
        </Button>,
      ]}
    >
      {formValues?.user ? (
        <Card style={{ marginBottom: "16px" }}>
          <Space size="small" direction="vertical">
            <Typography style={{}}>{formValues?.user?.name}</Typography>
            <Typography>{formValues?.user?.email}</Typography>
            <Typography>{formValues?.user?.phone}</Typography>
            <Typography>({formValues?.user?.host})</Typography>
          </Space>
        </Card>
      ) : null}

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(_, values) =>
          setFormValues((prevState) => ({ ...prevState, ...values }))
        }
      >
        <Form.Item
          name="title"
          label="Product title"
          rules={[{ required: true, message: "Please enter product title" }]}
        >
          <Input placeholder="e.g. Black Friday Drop" />
        </Form.Item>

        <Form.Item name="isExclusive" valuePropName="checked">
          <Switch
            checked={formValues?.isExclusive}
            onChange={(checked) => {
              form.setFieldsValue({
                isExclusive: checked,
              });

              setFormValues((prevState) => ({
                ...prevState,
                isExclusive: checked,
              }));
            }}
          />{" "}
          Mark as Exclusive
        </Form.Item>

        <Row style={{ gap: "16px" }}>
          <Form.Item
            style={{ flex: 1 }}
            name="platformSetup"
            label="Product Setup"
            rules={[
              {
                required:
                  mode === "add"
                    ? !formValues?.isExclusive ||
                      formValues?.tier?.length > 0 ||
                      formValues?.platformSetup?.length > 0
                    : false,
                message: "Please select product setup",
              },
            ]}
          >
            <Select
              disabled={mode !== "add"}
              onChange={() => {
                form.setFieldsValue({
                  tier: "tier1",
                });

                setFormValues((prevState) => ({
                  ...prevState,
                  tier: "tier1",
                }));
              }}
            >
              {formValues?.user ? (
                // show tagmango only when adding local product, hide in global
                <Select.Option value="tagmango">TagMango</Select.Option>
              ) : null}
              <Select.Option value="freedom">Freedom</Select.Option>
              <Select.Option value="enterprise">Enterprise</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            style={{ flex: 1 }}
            name="tier"
            label="Plan"
            rules={[
              {
                required:
                  mode === "add"
                    ? !formValues?.isExclusive ||
                      formValues?.platformSetup?.length > 0 ||
                      formValues?.tier?.length > 0
                    : false,
                message: "Please select plan",
              },
            ]}
          >
            <Select
              disabled={mode !== "add"}
              onChange={(value) => {
                if (value === "tier1") {
                  // remove subscription fee
                  form.setFieldsValue({
                    subscriptionFee: "",
                    totalProductPrice: calculateTotalPrice(
                      formValues?.onetimeFee || 0,
                      0
                    ),
                  });

                  setFormValues((prevState) => ({
                    ...prevState,
                    subscriptionFee: 0,
                    totalProductPrice: calculateTotalPrice(
                      formValues?.onetimeFee || 0,
                      0
                    ),
                  }));
                }
              }}
            >
              <Select.Option value="tier1">Basic</Select.Option>
              {formValues?.platformSetup ===
              "tagmango" ? null : formValues?.platformSetup === "freedom" ? (
                <>
                  <Select.Option value="tier2">Pro</Select.Option>
                  <Select.Option value="tier3">Advance</Select.Option>
                </>
              ) : (
                <>
                  <Select.Option value="tier2">Pro</Select.Option>
                  <Select.Option value="tier3">Advance</Select.Option>
                  <Select.Option value="tier4">Ultimate</Select.Option>
                </>
              )}
            </Select>
          </Form.Item>
        </Row>

        {["tagmango"].includes(formValues?.platformSetup) ? null : (
          <Form.Item
            name="onetimeFee"
            label="One-time fee"
            rules={[{ required: true, message: "Please enter one-time fee" }]}
          >
            <Input
              prefix="Rs."
              type="number"
              placeholder="25000"
              onChange={(e) =>
                form.setFieldsValue({
                  totalProductPrice: calculateTotalPrice(
                    e.target.value,
                    formValues?.subscriptionFee || 0
                  ),
                })
              }
            />
          </Form.Item>
        )}

        {["tier2", "tier3", "tier4"].includes(formValues?.tier) ? (
          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="subscriptionFee"
              label="Subscription fee"
              rules={[
                { required: true, message: "Please enter subscription fee" },
              ]}
            >
              <Input
                prefix="Rs."
                type="number"
                placeholder="25000"
                onChange={(e) =>
                  form.setFieldsValue({
                    totalProductPrice: calculateTotalPrice(
                      formValues?.onetimeFee || 0,
                      e.target.value
                    ),
                  })
                }
              />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name="paymentPlan"
              label="Payment plan"
              rules={[
                { required: true, message: "Please select payment plan" },
              ]}
            >
              <Select>
                <Select.Option value="monthly">Monthly</Select.Option>
                <Select.Option value="quarterly">Quarterly</Select.Option>
                <Select.Option value="halfyearly">Half-Yearly</Select.Option>
                <Select.Option value="yearly">Yearly</Select.Option>
              </Select>
            </Form.Item>
          </Row>
        ) : null}

        {["tagmango"].includes(formValues?.platformSetup) ? null : (
          <Form.Item label="Total product price" name="totalProductPrice">
            <Input value={calculateTotalPrice()} disabled />
          </Form.Item>
        )}

        <Form.Item
          name="productLink"
          label="Product purchase link (Only one-time mangoes eligible)"
          rules={[{ required: true, message: "Please enter purchase link" }]}
        >
          <Input placeholder="Enter purchase link" />
        </Form.Item>

        {["tagmango"].includes(formValues?.platformSetup) ? null : (
          <Form.Item
            name="mango"
            label="Mango ID"
            rules={[
              {
                required: ![
                  "65dc41e3d785b760b33962dd", // tagmango freedom (testing)
                  "6511822e2ca936d3ac3aeafe", // tagmango freedom (prod)
                  "660273b8febbc4230cbec8d0", // tagmango enterprise (testing)
                  "64be75a8bcae92064e321ced", // tagmango enterprise (prod)
                ].includes(formValues.mango),
                message: "Please enter mango id",
              },
            ]}
          >
            <Input
              placeholder="Enter the mango id"
              disabled={[
                "65dc41e3d785b760b33962dd", // tagmango freedom (testing)
                "6511822e2ca936d3ac3aeafe", // tagmango freedom (prod)
                "660273b8febbc4230cbec8d0", // tagmango enterprise (testing)
                "64be75a8bcae92064e321ced", // tagmango enterprise (prod)
              ].includes(formValues.mango)}
            />
          </Form.Item>
        )}

        <Form.Item
          name="revenueShare"
          label="Revenue share"
          rules={[
            {
              required: {
                value: true,
                message: "Please enter revenue share",
              },
              type: "number",
              min: 0,
              max: 100,
            },
          ]}
          normalize={(value) => (value ? Number(value) : undefined)}
        >
          <Input
            placeholder="Enter revenue share"
            type="number"
            style={{ MozAppearance: "textfield" }}
            onWheel={(e) => e.target.blur()}
            suffix="%"
          />
        </Form.Item>

        <Form.Item
          name="status"
          label="Change listing status"
          rules={[
            {
              required: !(
                formValues.isReferEarnSuspendedProduct ||
                ["deprecated", "suspended"].includes(formValues.status) ||
                [
                  "65dc41e3d785b760b33962dd", // tagmango freedom (testing)
                  "6511822e2ca936d3ac3aeafe", // tagmango freedom (prod)
                  "660273b8febbc4230cbec8d0", // tagmango enterprise (testing)
                  "64be75a8bcae92064e321ced", // tagmango enterprise (prod)
                ].includes(formValues.mango)
              ),
              message: "Please select status",
            },
          ]}
        >
          <Select
            disabled={
              formValues.isReferEarnSuspendedProduct ||
              ["deprecated", "suspended"].includes(formValues.status) ||
              [
                "65dc41e3d785b760b33962dd", // tagmango freedom (testing)
                "6511822e2ca936d3ac3aeafe", // tagmango freedom (prod)
                "660273b8febbc4230cbec8d0", // tagmango enterprise (testing)
                "64be75a8bcae92064e321ced", // tagmango enterprise (prod)
              ].includes(formValues.mango)
            }
          >
            <Select.Option value="listed">Listed</Select.Option>
            <Select.Option value="hidden">Hidden</Select.Option>
            <Select.Option value="expired">Expired</Select.Option>
          </Select>
        </Form.Item>

        {["listed", "hidden"].includes(formValues?.status) ? (
          <Form.Item label="Enable validity">
            <Switch checked={enableValidity} onChange={setEnableValidity} />
          </Form.Item>
        ) : null}

        {enableValidity && (
          <Form.Item
            name="expireAt"
            label="Valid till"
            rules={[{ required: true, message: "Please select expiry date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(current) =>
                current && current < moment().add(1, "d").startOf("day")
              }
              allowClear
              format="DD MMM YYYY"
              onChange={(date) =>
                form.setFieldsValue({
                  expireAt: date.endOf("day"),
                })
              }
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
RefernEarnAddEditProductModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["add", "edit"]),
  product: PropTypes.shape({
    _id: PropTypes.string,
    productName: PropTypes.string,
    isExclusive: PropTypes.bool,
    platformSetup: PropTypes.string,
    tier: PropTypes.string,
    onetimeFee: PropTypes.number,
    subscriptionFee: PropTypes.number,
    paymentPlan: PropTypes.string,
    productLink: PropTypes.string,
    mango: PropTypes.string,
    revenueShare: PropTypes.number,
    status: PropTypes.string,
    expireAt: PropTypes.string,
    user: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      email: PropTypes.string,
      host: PropTypes.string,
    }),
  }),
};

export default RefernEarnAddEditProductModal;
